/**
 * src/components/ui/Plegable.jsx
 * Una tabla larga que llega cerrada, con su estado escrito en la tapa.
 *
 * Pedido por Manuel el 04-09 para el historial de ejecuciones de la ficha. La
 * petición era «plegada por defecto, con un botón para desplegar», y añadió
 * después «que se abra sola cuando haya errores».
 *
 * ⚠️⚠️ LA REGLA QUE JUSTIFICA EL COMPONENTE: **plegar no puede tapar una mala
 * noticia.** Una tabla cerrada sobre un fallo es un fallo mudo (§5), y además
 * del peor tipo: la pantalla parece tranquila. Por eso `resumen` es un prop
 * REQUERIDO —la tapa SIEMPRE dice qué hay dentro— y por eso existe `motivo`,
 * que la abre sola cuando lo de dentro reclama mirarse.
 *
 * ⚠️ El detalle que hace que `motivo` no sea un incordio: se obedece SOLO en el
 * flanco, cuando pasa de falso a cierto. La tarjeta de la cola se refresca cada
 * 10 s; con un `abierta = motivo` a secas, cerrarla a mano sería imposible —se
 * volvería a abrir al siguiente refresco, cada diez segundos, para siempre—.
 * Aquí la abre la aparición del problema, y a partir de ahí manda quien mira.
 */
import { useEffect, useRef, useState } from 'react'

/**
 * @param {React.ReactNode} resumen  qué hay dentro. REQUERIDO: es la tapa, y
 *                                   tiene que bastar para no abrirla.
 * @param {boolean} [motivo]         cuando pasa de false a true, se abre sola.
 * @param {boolean} [inicial=false]  si empieza abierta.
 * @param {React.ReactNode} children lo que se pliega.
 */
export function Plegable({ resumen, motivo = false, inicial = false, children }) {
  const [abierta, setAbierta] = useState(inicial)
  // ⚠️ Arranca en `false`, NO en `motivo`. Con `useRef(motivo)` el primer render
  // se daba a sí mismo por «ya visto» y una tarjeta que nacía con errores no se
  // abría jamás —justo el caso que Manuel pidió—. Lo cazó la prueba de la
  // columna «Resultado», que dejó de encontrar la tabla.
  const motivoPrevio = useRef(false)

  useEffect(() => {
    // Solo el flanco de subida. Si `motivo` sigue siendo cierto y la persona la
    // cerró a mano, se queda cerrada: ya la ha visto.
    if (motivo && !motivoPrevio.current) setAbierta(true)
    motivoPrevio.current = motivo
  }, [motivo])

  return (
    <div>
      <button
        type="button"
        data-plegable="tapa"
        aria-expanded={abierta}
        onClick={() => setAbierta(!abierta)}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          width: '100%', textAlign: 'left', cursor: 'pointer',
          background: 'none', border: 'none', padding: 'var(--space-1) 0',
          fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
          fontFamily: 'inherit',
        }}
      >
        {/* El mismo signo que ya usan los días de la línea de tiempo de
            Ejecuciones: si el panel pliega en dos sitios, que se pliegue igual. */}
        <span aria-hidden="true" style={{ color: 'var(--color-text-3)' }}>
          {abierta ? '▾' : '▸'}
        </span>
        <span>{resumen}</span>
      </button>
      {abierta && <div style={{ marginTop: 'var(--space-2)' }}>{children}</div>}
    </div>
  )
}
